/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the component that provides the basis for all routes.
 * --------------------------------------------------------------------------------
 */

import { FormDefinitionSingleResponse, FormsContext, FormTypeSingleResponse, IDataSingleResponse, IForm, LookupCollectionResponse } from '@ngt/forms';
import * as React from 'react';
import { useContext } from 'react';
import { RouteProps } from 'react-router';
import { match, Route, useParams } from 'react-router-dom';
import { Configuration } from 'swr/dist/types';
import { IPatientForm } from '../../apis/dtos';
import { IPatientFormMapping } from '../../utilities/IPatientFormMapping';
import PatientFormByPatientIdAndRepeatResolver from '../Resolvers/PatientFormByPatientIdAndRepeatResolver';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

/**
 * This interface defines the properties for the OpmsFormByIdRoute component.
 */
export interface IPatientFormByPatientIdAndRepeatRouteProps extends RouteProps {
    formDefinitionCode?: string;
    patientId?: number;
    formRepeat?: number;

    computedMatch?: match<Record<string, string>>;

    /**
     * This property determines whether the associated data should be loaded before loading the route.
     */
    resolveBeforeLoad?: boolean | null;

    formMapping?: Record<string, IPatientFormMapping>;

    patientConfiguration?: Partial<Configuration<IDataSingleResponse<IForm>, IDataSingleResponse<IForm>>>;
    formDefinitionConfiguration?: Partial<Configuration<FormDefinitionSingleResponse, FormDefinitionSingleResponse>>;
    lookupConfiguration?: Partial<Configuration<LookupCollectionResponse, LookupCollectionResponse>>;
    formConfiguration?: Partial<Configuration<IDataSingleResponse<IPatientForm>, IDataSingleResponse<IPatientForm>>>;
    formTypeConfiguration?: Partial<Configuration<FormTypeSingleResponse, FormTypeSingleResponse>>;
}

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

/**
 * This component provides context for the form management system.
 * @param param0 component properties.
 */
const PatientFormByPatientIdAndRepeatRoute: React.FunctionComponent<IPatientFormByPatientIdAndRepeatRouteProps> = ({
    formDefinitionCode,
    patientId,
    formRepeat,
    resolveBeforeLoad,
    children,
    formMapping,

    patientConfiguration,
    formConfiguration,
    lookupConfiguration,
    formDefinitionConfiguration,
    formTypeConfiguration,

    ...routeProps
}) => {
    const routeParams = useParams<Record<string, string>>();
    const formsContext = useContext(FormsContext);
    const params = routeProps.computedMatch?.params ?? routeParams;
    const paramPatientIdStr = params['patientId'];
    const paramFormRepeatStr = params[formsContext.routeParameters.formRepeat];
    const paramFormDefCode = params[formsContext.routeParameters.formDefinitionCode];
    let paramFormRepeat = null;
    let paramPatientId = null;

    try {
        if (paramFormRepeatStr) {
            paramFormRepeat = parseInt(paramFormRepeatStr);
        }
    }
    catch (error) {
        console.error(`Invalid Form Repeat in route: ${paramFormRepeatStr}`)
    }

    try {
        if (paramPatientIdStr) {
            paramPatientId = parseInt(paramPatientIdStr);
        }
    }
    catch (error) {
        console.error(`Invalid Patient ID in route: ${paramPatientIdStr}`)
    }

    return <Route {...routeProps}>
        <PatientFormByPatientIdAndRepeatResolver
            formDefinitionCode={paramFormDefCode ?? formDefinitionCode ?? null}
            patientId={paramPatientId ?? patientId ?? null}
            formRepeat={paramFormRepeat ?? formRepeat ?? null}
            resolveBeforeLoad={resolveBeforeLoad}
            formMapping={formMapping}
            formConfiguration={formConfiguration}
            formDefinitionConfiguration={formDefinitionConfiguration}
            formTypeConfiguration={formTypeConfiguration}
            lookupConfiguration={lookupConfiguration}
            patientConfiguration={patientConfiguration}
        >
            {children}
        </PatientFormByPatientIdAndRepeatResolver>
    </Route>
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientFormByPatientIdAndRepeatRoute;